// ── OCR ↔ 홈택스 카드 매칭 ──
// 같은 거래로 추정되는 두 transaction을 찾아 영수증 사진을 카드 거래에 인계.
// 매칭 기준: 사업자번호/거래처명 + 날짜 + 금액(±100원)

import { doc, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../db';

/**
 * 거래 금액 정규화 — supply + vat (taxFreeAmount는 supply에 이미 포함)
 */
function txTotal(t) {
  return (Number(t.supply) || 0) + (Number(t.vat) || 0);
}

/**
 * 사업자번호 정규화 (숫자만)
 */
function normBiz(s) {
  if (!s) return null;
  const cleaned = String(s).replace(/[^\d]/g, '');
  return cleaned.length === 10 ? cleaned : null;
}

/**
 * 거래처명 정규화 — 공백/특수문자 제거, 소문자
 */
function normName(s) {
  if (!s) return '';
  return String(s).replace(/\s+/g, '').replace(/[()（）주식회사（주）]/g, '').toLowerCase();
}

/**
 * 두 거래처명 유사도 (0~1) — 단순 포함 + 길이 비율
 */
function nameScore(a, b) {
  const na = normName(a);
  const nb = normName(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) {
    const shortLen = Math.min(na.length, nb.length);
    const longLen = Math.max(na.length, nb.length);
    return shortLen / longLen;
  }
  return 0;
}

/**
 * 매칭 후보 페어 찾기
 * @param {Array} transactions  전체 거래장부
 * @returns {Array<{ocrTx, cardTx, score, reason}>}
 */
export function findMatches(transactions) {
  // OCR 출처 거래 (사진 있을 수 있음)
  const ocrTxns = transactions.filter(t =>
    t?.type === 'expense' && t?.source === 'ocr'
  );
  // 홈택스 엑셀에서 들어온 카드 거래 (사진 없을 가능성 높음)
  const cardTxns = transactions.filter(t =>
    t?.type === 'expense' && t?.source === 'hometax_excel'
  );

  if (ocrTxns.length === 0 || cardTxns.length === 0) return [];

  const matches = [];
  const usedCardIds = new Set();

  for (const ocr of ocrTxns) {
    const ocrTotal = txTotal(ocr);
    const ocrBiz = normBiz(ocr.partnerBizNumber);
    const ocrDate = ocr.date;

    let best = null;
    for (const card of cardTxns) {
      if (usedCardIds.has(card.id)) continue;
      if (card.date !== ocrDate) continue;

      const cardTotal = txTotal(card);
      const diff = Math.abs(ocrTotal - cardTotal);
      if (diff > 100) continue;

      const cardBiz = normBiz(card.partnerBizNumber);

      let score = 0;
      let reason = '';

      // 1차: 사업자번호 일치
      if (ocrBiz && cardBiz && ocrBiz === cardBiz) {
        score = 1.0;
        reason = '사업자번호·날짜·금액 일치';
      } else {
        // 2차: 거래처명 유사도
        const ns = nameScore(ocr.partnerName, card.partnerName);
        if (ns >= 0.7) {
          score = ns * 0.9;
          reason = ns === 1
            ? '거래처명·날짜·금액 일치'
            : `거래처명 유사(${Math.round(ns * 100)}%)·날짜·금액 일치`;
        }
      }

      if (score > 0 && (!best || score > best.score)) {
        best = { ocrTx: ocr, cardTx: card, score, reason, diff };
      }
    }

    if (best) {
      matches.push(best);
      usedCardIds.add(best.cardTx.id);
    }
  }

  // 점수 높은 순 정렬
  return matches.sort((a, b) => b.score - a.score);
}

/**
 * 매칭 적용 — OCR 거래의 영수증 사진을 카드 거래에 인계 + OCR 거래 삭제
 * @param {string} uid
 * @param {Array<{ocrTx, cardTx}>} pairs  사용자가 승인한 페어들
 */
export async function applyMatches(uid, pairs) {
  if (!uid) throw new Error('uid required');
  let applied = 0;
  let skipped = 0;
  const errors = [];

  for (const { ocrTx, cardTx } of pairs) {
    try {
      // 카드 거래에 영수증 사진 + lineItems 인계
      const patch = {
        updatedAt: serverTimestamp(),
      };
      if (ocrTx.receiptImageUrl) {
        patch.receiptImageUrl = ocrTx.receiptImageUrl;
        patch.receiptImagePath = ocrTx.receiptImagePath || null;
      }
      // OCR이 가진 lineItems (식재료 사용량 데이터)도 카드 거래에 인계
      if (Array.isArray(ocrTx.lineItems) && ocrTx.lineItems.length > 0) {
        patch.lineItems = ocrTx.lineItems;
      }
      // 메모 통합 — 카드 메모 + OCR 메모
      if (ocrTx.memo && ocrTx.memo !== cardTx.memo) {
        const cardMemo = cardTx.memo || '';
        patch.memo = `${cardMemo}\n[병합됨: OCR] ${ocrTx.memo.replace(/^\[OCR\][\s·]*/, '')}`.trim();
      }
      // 의제매입 후보 정보도 인계
      if (ocrTx.aiSuggestedPresumed) patch.aiSuggestedPresumed = true;
      if (ocrTx.taxFreeAmount && Number(ocrTx.taxFreeAmount) > 0) {
        patch.taxFreeAmount = ocrTx.taxFreeAmount;
      }

      // 카드 거래 update
      const cardRef = doc(db, 'users', uid, 'transactions', cardTx.id);
      await setDoc(cardRef, patch, { merge: true });

      // OCR 거래 삭제
      const ocrRef = doc(db, 'users', uid, 'transactions', ocrTx.id);
      await deleteDoc(ocrRef);

      applied += 1;
    } catch (e) {
      console.error('매칭 적용 실패:', e);
      errors.push({ ocrId: ocrTx.id, cardId: cardTx.id, error: e.message });
      skipped += 1;
    }
  }

  return { applied, skipped, errors };
}
